/**
 * The four-colour brand ribbon — green, yellow, blue, red.
 *
 * A thin rule of the four board colours along an edge — the top of the footer,
 * the foot of a panel. It is the only place the whole palette appears at once,
 * and the only place blue enters the site outside the logo (THEME.md §3).
 *
 * THIN IS THE WHOLE POINT. A `corner` variant existed briefly: the same bands
 * scaled across a hero corner with `preserveAspectRatio="none"`. Over a ~800px
 * hero it stretched into a full-height slab of four colours and looked awful.
 * If a bolder ribbon is ever wanted, it must be a BOUNDED element (fixed width
 * AND height, aspect preserved), and it must be checked at the real proportions
 * of the thing it sits in — not in a convenient test frame.
 *
 * Purely decorative (`aria-hidden`), carrying no information, so the board's
 * yellow runs at full strength despite being 1.51:1 on white.
 */

const BANDS = ['#004D40', '#FFC107', '#00AEEF', '#DF2F2B'];

export function RibbonEdge({ className = '', height = 6 }: { className?: string; height?: number }) {
  const w = 100 / BANDS.length;
  const slant = 10;
  return (
    <svg
      viewBox="0 0 100 10"
      preserveAspectRatio="none"
      className={className}
      style={{ height, width: '100%', display: 'block' }}
      aria-hidden="true"
      focusable="false"
    >
      {BANDS.map((fill, i) => {
        const x = i * w;
        // Bleed the outer bands off-canvas and overlap the inner seams, so
        // sub-pixel rounding cannot open a hairline gap when stretched.
        const l = i === 0 ? -slant : 0;
        const r = i === BANDS.length - 1 ? slant : 0.4;
        return (
          <polygon
            key={fill}
            fill={fill}
            points={`${x + slant + l},0 ${x + w + slant + r},0 ${x + w + r},10 ${x + l},10`}
          />
        );
      })}
    </svg>
  );
}

export default RibbonEdge;
